import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listInventoryMovements } from "@/lib/inventory-views.functions";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { FilterBar } from "@/components/ui/filter-bar";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { StatusPill, type StatusTone } from "@/components/ui/status-pill";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";
import { subDays, format } from "date-fns";

export const Route = createFileRoute("/_authenticated/app/inventory/movements")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: MovementsPage,
});

const KIND_TONE: Record<string, StatusTone> = {
  receipt: "success", pick: "warning", ship: "info",
  adjustment: "danger", transfer: "neutral", return: "info",
};

type Row = {
  id: string; kind: string; qty_delta: number; sku_id: string;
  location_id: string | null; lot: string | null; ref_table: string | null;
  created_at: string; customer_id: string | null;
};

function MovementsPage() {
  const { tenantId, facilityId, customerId } = useScope();
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sort = urlSearch.sort ?? "desc";
  // Movements is a historical ledger — default to last 30d when user hasn't picked a range.
  const effectiveStart = urlSearch.start ?? format(subDays(new Date(), 29), "yyyy-MM-dd");
  const effectiveEnd = urlSearch.end ?? format(new Date(), "yyyy-MM-dd");
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: effectiveEnd }),
    [effectiveStart, effectiveEnd],
  );
  const listMovementsFn = useServerFn(listInventoryMovements);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<string>("all");

  const { data: bundle, isLoading } = useQuery({
    queryKey: ["ledger-movements", tenantId, facilityId, customerId, fromIso, toIso, sort],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listMovementsFn({ data: {
      tenantId: tenantId!, facilityId: facilityId!, customerId: customerId ?? null,
      fromIso, toIso, sort,
    } }),
    placeholderData: keepPreviousData,
  });
  const rows = (bundle?.rows ?? []) as Row[];
  const skus = bundle?.skus ?? [];
  const locs = bundle?.locations ?? [];

  const skuMap = useMemo(() => Object.fromEntries(skus.map((s) => [s.id, s])), [skus]);
  const locMap = useMemo(() => Object.fromEntries(locs.map((l) => [l.id, l.code])), [locs]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (kind !== "all" && r.kind !== kind) return false;
    if (!q) return true;
    const s = skuMap[r.sku_id];
    const term = q.toLowerCase();
    return (s?.sku_code?.toLowerCase().includes(term) ?? false)
      || (s?.name?.toLowerCase().includes(term) ?? false)
      || ((r.location_id && locMap[r.location_id]?.toLowerCase().includes(term)) ?? false)
      || ((r.lot ?? "").toLowerCase().includes(term));
  }), [rows, q, kind, skuMap, locMap]);

  const exportCsv = () => {
    const header = ["timestamp", "kind", "sku", "name", "location", "lot", "qty_delta", "ref"];
    const lines = [header.join(",")];
    for (const r of filtered) {
      const s = skuMap[r.sku_id];
      const cell = (v: string | number | null | undefined) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      lines.push([
        new Date(r.created_at).toISOString(), r.kind, s?.sku_code ?? "", s?.name ?? "",
        r.location_id ? locMap[r.location_id] ?? "" : "", r.lot ?? "", r.qty_delta, r.ref_table ?? "",
      ].map(cell).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `movements-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const cols: ColumnDef<Row, unknown>[] = [
    { id: "when", header: "When", accessorKey: "created_at", cell: ({ row }) => <span className="text-xs text-muted-foreground whitespace-nowrap">{new Date(row.original.created_at).toLocaleString()}</span> },
    { id: "kind", header: "Kind", accessorKey: "kind", cell: ({ row }) => <StatusPill tone={KIND_TONE[row.original.kind] ?? "neutral"} dot>{row.original.kind}</StatusPill> },
    { id: "sku", header: "SKU", accessorFn: (r) => skuMap[r.sku_id]?.sku_code ?? "", cell: ({ row }) => {
      const s = skuMap[row.original.sku_id];
      return s ? <div><div className="font-mono text-sm">{s.sku_code}</div><div className="text-xs text-muted-foreground truncate max-w-[200px]">{s.name}</div></div> : <span className="text-muted-foreground">—</span>;
    } },
    { id: "loc", header: "Location", cell: ({ row }) => <span className="font-mono text-xs">{row.original.location_id ? locMap[row.original.location_id] ?? "—" : "—"}</span> },
    { id: "lot", header: "Lot", cell: ({ row }) => <span className="font-mono text-xs text-muted-foreground">{row.original.lot ?? "—"}</span> },
    { id: "delta", header: "Δ Qty", accessorKey: "qty_delta", cell: ({ row }) => {
      const positive = (row.original.qty_delta ?? 0) > 0;
      return <span className={`tabular-nums font-medium ${positive ? "text-success" : "text-destructive"}`}>{positive ? "+" : ""}{row.original.qty_delta}</span>;
    } },
    { id: "ref", header: "Reference", cell: ({ row }) => <span className="text-xs text-muted-foreground">{row.original.ref_table ?? "—"}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Inventory Movements"
        description="Most recent 500 ledger entries across the selected scope."
        actions={<Button variant="outline" size="sm" onClick={exportCsv} disabled={filtered.length === 0}><Download className="h-4 w-4 mr-2" />Export CSV</Button>}
        meta={<span className="text-xs text-muted-foreground">{filtered.length} movement{filtered.length === 1 ? "" : "s"}</span>}
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <FilterBar search={q} onSearchChange={setQ} searchPlaceholder="Search SKU, location, lot…">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="receipt">Receipt</SelectItem>
              <SelectItem value="pick">Pick</SelectItem>
              <SelectItem value="ship">Ship</SelectItem>
              <SelectItem value="adjustment">Adjustment</SelectItem>
              <SelectItem value="transfer">Transfer</SelectItem>
              <SelectItem value="return">Return</SelectItem>
            </SelectContent>
          </Select>
          <DateRangeFilter
            value={{ start: urlSearch.start ?? effectiveStart, end: urlSearch.end ?? effectiveEnd }}
            onChange={(r) => navigate({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
          />
          <SortDirectionToggle
            value={sort}
            onChange={(s) => navigate({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
          />
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(r) => r.id}
          empty={{ title: rows.length > 0 ? "No matches" : "No movements yet", description: "Receive, pick, ship, or transfer inventory to populate the ledger." }}
          mobileCard={(r) => {
            const s = skuMap[r.sku_id];
            const positive = (r.qty_delta ?? 0) > 0;
            return (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <StatusPill tone={KIND_TONE[r.kind] ?? "neutral"} dot>{r.kind}</StatusPill>
                  <span className={`tabular-nums font-semibold ${positive ? "text-success" : "text-destructive"}`}>{positive ? "+" : ""}{r.qty_delta}</span>
                </div>
                <div className="text-sm font-mono">{s?.sku_code ?? "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{s?.name ?? ""}</div>
                <div className="text-[11px] text-muted-foreground">
                  {r.location_id ? `${locMap[r.location_id]} · ` : ""}{new Date(r.created_at).toLocaleString()}
                </div>
              </div>
            );
          }}
        />
      </div>
    </>
  );
}
